import React from 'react';
import Hero from '../components/Hero';
import { Card } from '../components/ui/card';
import { 
  Target, 
  Users, 
  Award, 
  TrendingUp,
  CheckCircle2,
  Leaf,
  Snowflake,
  Wrench
} from 'lucide-react';
import { companyInfo, galleryImages } from '../mockData';

const AboutPage = () => {
  const stats = [
    { number: '15+', label: 'Years Experience', icon: Award },
    { number: '200+', label: 'Properties Managed', icon: Target },
    { number: '50+', label: 'Commercial Clients', icon: Users },
    { number: '99%', label: 'Client Retention', icon: TrendingUp }
  ];

  const values = [
    {
      title: 'Reliability',
      description: 'We show up when we say we will. Our service delivery is consistent, documented, and dependable—season after season.',
      icon: CheckCircle2
    },
    {
      title: 'Professionalism',
      description: 'From our trained crews to our commercial-grade equipment, every aspect of our operation reflects professional standards.',
      icon: Award
    },
    {
      title: 'Responsiveness',
      description: 'Direct communication, rapid response times, and proactive management. When you need us, we are there.',
      icon: Target
    },
    {
      title: 'Expertise',
      description: 'We specialize exclusively in commercial properties. Our team understands the unique demands of professional property management.',
      icon: Users
    }
  ];

  const timeline = [
    {
      year: '2008',
      title: 'Founded',
      description: 'Started with a focus on commercial landscape maintenance for local retail properties.',
      icon: Leaf
    },
    {
      year: '2012',
      title: 'Snow Division',
      description: 'Expanded to offer comprehensive winter services with 24/7 storm response capabilities.',
      icon: Snowflake
    },
    {
      year: '2018',
      title: 'Multi-Site Management',
      description: 'Developed systems to manage property portfolios across multiple locations.',
      icon: Wrench
    },
    {
      year: '2024',
      title: 'Regional Leader',
      description: 'Now serving 200+ commercial properties with a team dedicated to professional standards.',
      icon: TrendingUp
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <Hero
        subtitle="About Us"
        title="Professional Standards. Commercial Focus."
        description="We've built our business on a simple principle: commercial properties deserve specialized care from professionals who understand their unique needs."
        primaryCTA={{ text: 'Get Started', link: '/contact' }}
        image="https://images.unsplash.com/photo-1627549569239-0d1119c96f1e"
        height="min-h-[500px]"
      />

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-primary mb-6 text-center">
              Who We Are
            </h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                Illyria Landscape Management was founded on the belief that commercial properties 
                require a different level of service than residential accounts. Property managers, 
                facility directors, and ownership groups need reliable partners who understand the 
                stakes—your property's presentation affects your business, your tenants, and your 
                bottom line.
              </p>
              <p>
                Based in Stouffville, Ontario, we proudly serve commercial properties throughout 
                Toronto and the Greater Toronto Area. Our focus on exclusively commercial clients 
                means we understand your timelines, your expectations, and your need for responsive, 
                professional service. We're not juggling residential accounts on the side—our entire 
                operation is built around the demands of commercial property management.
              </p>
              <p>
                From landscape maintenance to snow removal, seasonal cleanups to ongoing site upkeep, 
                we provide comprehensive exterior services that protect your property investment. Our 
                trained crews use commercial-grade equipment, follow documented protocols, and deliver 
                the kind of consistent service you can count on year-round.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-navy-primary text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-ice-blue/20 mb-4">
                    <IconComponent className="text-ice-blue" size={28} />
                  </div>
                  <div className="text-4xl sm:text-5xl font-bold mb-2">
                    {stat.number}
                  </div>
                  <div className="text-ice-blue/80">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-ice-blue/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-primary mb-4">
              Our Values
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              The principles that guide every service we deliver
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow duration-300 bg-white">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-ice-blue/50 flex items-center justify-center">
                      <IconComponent className="text-navy-primary" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-navy-primary mb-2">
                        {value.title}
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-primary mb-4">
              Our Journey
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Growing from a local service provider to a regional commercial maintenance leader
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {timeline.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="flex gap-6 group">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="w-14 h-14 rounded-full bg-navy-primary flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="text-white" size={24} />
                      </div>
                      {index < timeline.length - 1 && (
                        <div className="w-0.5 h-full bg-ice-blue/50 mt-2"></div>
                      )}
                    </div>
                    <div className="pb-12">
                      <div className="inline-block px-3 py-1 bg-ice-blue/30 rounded-full text-navy-primary font-bold text-sm mb-2">
                        {item.year}
                      </div>
                      <h3 className="text-2xl font-bold text-navy-primary mb-2">
                        {item.title}
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Project Gallery */}
      <section className="py-20 bg-ice-blue/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-primary mb-4">
              Our Work
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Professional maintenance that keeps commercial properties looking their best
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image) => (
              <div 
                key={image.id} 
                className="relative overflow-hidden rounded-lg aspect-[4/3] group cursor-pointer"
              >
                <img 
                  src={image.url} 
                  alt={image.title} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-primary/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <div className="p-4 text-white">
                    <div className="text-xs text-ice-blue mb-1">{image.category}</div>
                    <div className="font-semibold">{image.title}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
